import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { CalendarIcon, UserGroupIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface LabDayArchive {
  id: number;
  slug: string;
  title: string;
  description?: string;
  status: string;
  created_at: string;
  projects?: ProjectArchive[];
}

interface ProjectArchive {
  id: number;
  title: string;
  description: string;
  team_members: string;
  category?: string;
  isWinner?: boolean;
  nominatedCategories?: string[];
  voteCounts?: {
    Innovation: number;
    Execution: number;
  };
}

const Archive: React.FC = () => {
  const [labDays, setLabDays] = useState<LabDayArchive[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLabDay, setSelectedLabDay] = useState<LabDayArchive | null>(null);

  useEffect(() => {
    fetchLabDays();
  }, []);

  const fetchLabDays = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/archive`);
      setLabDays(response.data);
    } catch (error) {
      toast.error('Failed to load archive data');
      console.error('Error fetching archive:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLabDayDetails = async (slug: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/${slug}`);
      setSelectedLabDay(response.data);
    } catch (error) {
      toast.error('Failed to load Lab Day details');
      console.error('Error fetching Lab Day details:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shopware-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-shopware-dark-blue">
          Lab Days
        </h1>
        <p className="text-gray-600 mt-2">
          Current and past Lab Day events
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Lab Days List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md p-6">
            {/* Current Events */}
            {labDays.filter(labDay => labDay.status !== 'closed').length > 0 && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-shopware-dark-blue mb-4">
                  Current Events
                </h2>
                <div className="space-y-4">
                  {labDays.filter(labDay => labDay.status !== 'closed').map((labDay) => (
                    <button
                      key={labDay.id}
                      onClick={() => fetchLabDayDetails(labDay.slug)}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        selectedLabDay?.id === labDay.id
                          ? 'bg-shopware-blue text-white'
                          : 'bg-green-50 border border-green-200 text-shopware-dark-blue hover:bg-shopware-blue hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5" />
                        <span className="font-medium">{labDay.title}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          labDay.status === 'submission' ? 'bg-green-500 text-white' :
                          labDay.status === 'voting' ? 'bg-orange-500 text-white' : ''
                        }`}>
                          {labDay.status === 'submission' ? 'Open' : 
                           labDay.status === 'voting' ? 'Voting' : labDay.status}
                        </span>
                      </div>
                      <div className="text-sm mt-1 opacity-75">
                        {new Date(labDay.created_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Past Events */}
            {labDays.filter(labDay => labDay.status === 'closed').length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-shopware-dark-blue mb-4">
                  Past Events
                </h2>
                <div className="space-y-4">
                  {labDays.filter(labDay => labDay.status === 'closed').map((labDay) => (
                    <button
                      key={labDay.id}
                      onClick={() => fetchLabDayDetails(labDay.slug)}
                      className={`w-full text-left p-4 rounded-xl transition-all ${
                        selectedLabDay?.id === labDay.id
                          ? 'bg-shopware-blue text-white'
                          : 'bg-shopware-light-blue text-shopware-dark-blue hover:bg-shopware-blue hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-5 w-5" />
                        <span className="font-medium">{labDay.title}</span>
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-500 text-white">
                          Closed
                        </span>
                      </div>
                      <div className="text-sm mt-1 opacity-75">
                        {new Date(labDay.created_at).toLocaleDateString()}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Lab Day Details */}
        <div className="lg:col-span-2">
          {selectedLabDay ? (
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-shopware-dark-blue">
                    {selectedLabDay.title}
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {selectedLabDay.description}
                  </p>
                  <span className={`inline-block mt-2 text-xs px-3 py-1 rounded-full ${
                    selectedLabDay.status === 'submission' ? 'bg-green-500 text-white' :
                    selectedLabDay.status === 'voting' ? 'bg-orange-500 text-white' :
                    'bg-gray-500 text-white'
                  }`}>
                    {selectedLabDay.status === 'submission' ? 'Open for Submissions' : 
                     selectedLabDay.status === 'voting' ? 'Voting in Progress' : 'Closed'}
                  </span>
                </div>
                <Link
                  to={`/lab/${selectedLabDay.slug}`}
                  className="bg-shopware-light-blue text-shopware-dark-blue px-4 py-2 rounded-xl hover:bg-shopware-blue hover:text-white transition-all"
                >
                  View Details
                </Link>
              </div>

              {selectedLabDay.status !== 'closed' ? (
                // Current Events: Show project count and nominations
                <div className="space-y-6">
                  <div className="bg-shopware-light-blue rounded-xl p-4">
                    <h3 className="text-lg font-semibold text-shopware-dark-blue mb-2">
                      Current Status
                    </h3>
                    <div className="flex items-center gap-2 mb-4">
                      <UserGroupIcon className="h-5 w-5 text-shopware-blue" />
                      <span className="text-shopware-dark-blue">
                        {selectedLabDay.projects?.length || 0} Projects Submitted
                      </span>
                    </div>
                  </div>

                  {/* Nominations by Category */}
                  <div className="space-y-4">
                    {['Innovation', 'Execution'].map((category) => {
                      const nominatedProjects = selectedLabDay.projects?.filter(
                        project => project.nominatedCategories?.includes(category)
                      ) || [];

                      return (
                        <div key={category} className="border-t pt-4">
                          <h3 className="text-xl font-semibold text-shopware-dark-blue mb-4">
                            {category} Nominations ({nominatedProjects.length})
                          </h3>
                          {nominatedProjects.length > 0 ? (
                            <div className="space-y-3">
                              {nominatedProjects.map((project, index) => (
                                <div key={index} className="bg-gray-50 rounded-xl p-4">
                                  <h4 className="font-semibold text-shopware-dark-blue">
                                    {project.title}
                                  </h4>
                                  <p className="text-gray-600 mt-1 text-sm">
                                    {project.description}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                                    <UserGroupIcon className="h-4 w-4" />
                                    <span>{project.team_members}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500">No nominations yet</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                // Past Events: Show winners and votes
                <div className="space-y-6">
                  {['Innovation', 'Execution'].map((category) => {
                    // Find all projects with the highest vote count for this category
                    const projectsWithVotes = selectedLabDay.projects?.filter(
                      project => project.voteCounts && project.voteCounts[category as keyof typeof project.voteCounts] >= 0
                    ) || [];
                    
                    // Find the maximum vote count for this category
                    const maxVotes = Math.max(...projectsWithVotes.map(p => p.voteCounts?.[category as keyof typeof p.voteCounts] || 0));
                    
                    // Find all projects with the maximum vote count
                    const winningProjects = projectsWithVotes.filter(p => 
                      (p.voteCounts?.[category as keyof typeof p.voteCounts] || 0) === maxVotes && maxVotes > 0
                    );

                    return (
                      <div key={category} className="border-t pt-6">
                        <h3 className="text-xl font-semibold text-shopware-dark-blue mb-4">
                          {category} Winner{winningProjects.length > 1 ? 's' : ''}
                        </h3>
                        {winningProjects.length > 0 ? (
                          <div className="space-y-4">
                            {winningProjects.map((winningProject, index) => (
                              <div key={index} className="bg-shopware-light-blue rounded-xl p-4">
                                <div className="flex items-start gap-4">
                                  <TrophyIcon className="h-6 w-6 text-shopware-blue flex-shrink-0" />
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-shopware-dark-blue">
                                      {winningProject.title}
                                    </h4>
                                    <p className="text-gray-600 mt-1">
                                      {winningProject.description}
                                    </p>
                                    <div className="flex items-center gap-4 mt-2">
                                      <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <UserGroupIcon className="h-4 w-4" />
                                        <span>{winningProject.team_members}</span>
                                      </div>
                                      <div className="text-sm font-medium text-shopware-blue">
                                        {winningProject.voteCounts?.[category as keyof typeof winningProject.voteCounts] || 0} Votes
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500">No winner selected</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md p-6 text-center text-gray-500">
              Select a Lab Day to view its details
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Archive; 